/**
 * Pure helpers behind the hotel-restricted PDP calendar (VAM-65).
 *
 * Days are always local `YYYY-MM-DD` keys (`formatDateLocal`), never
 * `toISOString()`, which shifts to the previous day in CET. Key arithmetic runs
 * in UTC so a DST switch never adds or loses a day. The picker queries these
 * sets several times per cell on every redraw, so every lookup is a `Set` hit.
 */

import type { CheckInDates } from '@/services/api/player/getFilters';
import { buildDurationPlan, MAX_PRODUCT_DURATION } from '@/utils/filters/durationPlan';

const DAY_MS = 86_400_000;

function keyToUtc(key: string): number {
  const [year, month, day] = key.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
}

function utcToKey(ms: number): string {
  const date = new Date(ms);
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${date.getUTCFullYear()}-${month}-${day}`;
}

/** `key` shifted by `days`, e.g. `addDaysToKey('2026-10-24', 7)` → `'2026-10-31'`. */
export function addDaysToKey(key: string, days: number): string {
  return utcToKey(keyToUtc(key) + days * DAY_MS);
}

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export function daysBetweenKeys(from: string, to: string): number {
  return Math.round((keyToUtc(to) - keyToUtc(from)) / DAY_MS);
}

/** Local-midnight `Date` for a key, the frame the date picker works in. */
export function keyToLocalDate(key: string): Date {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Set of arrival-day keys from the mask, limited to `[from, to]` (inclusive).
 * The index is counted from the server's `TravelStartDatesFrom`, not from a
 * client-side "tomorrow".
 */
export function arrivalDaySet(data: CheckInDates, from: string, to: string): Set<string> {
  const days = new Set<string>();
  const firstIndex = Math.max(0, daysBetweenKeys(data.from, from));
  const lastIndex = Math.min(data.mask.length - 1, daysBetweenKeys(data.from, to));

  for (let index = firstIndex; index <= lastIndex; index++) {
    if (data.mask[index] === '1') {
      days.add(addDaysToKey(data.from, index));
    }
  }
  return days;
}

/** Departure-day keys for an arrival, from that arrival's allowed durations. */
export function departureDaySet(arrival: string, durations: number[]): Set<string> {
  return new Set(durations.map((nights) => addDaysToKey(arrival, nights)));
}

/** Smallest allowed duration, or `null` when none. */
export function minStay(durations: number[]): number | null {
  return durations.length > 0 ? Math.min(...durations) : null;
}

/**
 * Whether any length the search would try for this preset is sold by the hotel:
 * the preset ± `flex` plus its nearest whole week, exactly the set
 * {@link buildDurationPlan} widens to. `flex = 0` means an exact match only.
 * An empty `offered` list (failed or empty request) hides nothing.
 */
export function isPresetOffered(preset: number, offered: number[], flex: number): boolean {
  if (offered.length === 0) return true;
  return buildDurationPlan(preset, MAX_PRODUCT_DURATION, flex).durations.some((nights) =>
    offered.includes(nights)
  );
}

/**
 * Largest offered duration that still fits a window of `nights`, or `null` when
 * none does. Used as the preset fallback instead of the page default, so a
 * hotel selling only 6 nights never gets a "7 days" chip.
 */
export function largestOfferedWithin(offered: number[], nights: number): number | null {
  const fitting = offered.filter((value) => value <= nights);
  return fitting.length > 0 ? Math.max(...fitting) : null;
}
